import React from "react";
import Head from "next/head";
import Link from "next/link";
import { GetServerSideProps } from "next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircleIcon from "@mui/icons-material/Circle";
import { List, ListItem, ListItemIcon, useTheme } from "@mui/material";

import { SERVER } from "../../../../src/common/config/utils";
import useCourseDetail from "../../../../src/learn/context/course-detail/useCourseDetail";
import ContentLayout from "../../../../src/learn/components/layout/ContentLayout";
import useActiveModule from "../../../../src/learn/context/active-module/useActiveModule";
import useActiveTopic from "../../../../src/learn/context/active-topic/useActiveTopic";
import { OG_IMAGE } from "../../../../src/learn/utils/constants";
import { SITE_NAME } from "../../../../src/common/config/seo";
import PageError, {
  IOnError,
  onError,
} from "../../../../src/common/components/error-handlers/PageError";

export default function ({
  module,
  params,
  error,
}: {
  module: NSCourses.IModuleProps;
  params: {
    courseSlug: string;
    moduleSlug: string;
  };
  error: IOnError;
}) {
  const theme = useTheme();
  const { courseDetail } = useCourseDetail();
  const { activeModule } = useActiveModule();
  const { setActiveTopic } = useActiveTopic();

  if (error) {
    return <PageError error={error} />;
  }

  return (
    <>
      <Head>
        <title>
          {module?.name} | {SITE_NAME}
        </title>
        <meta
          name="description"
          content={module?.description || courseDetail?.meta_description}
        />
        <link
          rel="canonical"
          href={`${SERVER}/${params?.courseSlug}/${params?.moduleSlug}`}
        />
        <meta
          property="og:url"
          content={`${SERVER}/${params?.courseSlug}/${params?.moduleSlug}`}
        />
        <meta property="og:title" content={`${module?.name} | ${SITE_NAME}`} />
        <meta
          property="og:description"
          content={module?.description || courseDetail?.meta_description}
        />
        <meta
          property="og:image"
          content={courseDetail?.og_image || OG_IMAGE}
        />
        {/* twitter tags */}
        <meta
          property="twitter:url"
          content={`${SERVER}/${params?.courseSlug}/${params?.moduleSlug}`}
        />
        <meta name="twitter:title" content={`${module?.name} | ${SITE_NAME}`} />
        <meta
          name="twitter:description"
          content={module?.description || courseDetail?.meta_description}
        />
        <meta
          name="twitter:image"
          content={courseDetail?.og_image || OG_IMAGE}
        />
      </Head>
      <ContentLayout>
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontSize: 36,
              [theme.breakpoints.down("sm")]: { fontSize: 24 },
            }}
          >
            {module?.name}
          </Typography>
          <Divider sx={{ my: 4 }} />
          {module?.description && (
            <Typography pt={2}>{module?.description}</Typography>
          )}
          <List disablePadding>
            {activeModule?.topics.map((topic) =>
              topic?.route ? (
                <ListItem
                  key={topic.id}
                  sx={{
                    a: {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 14 }}>
                    <CircleIcon sx={{ fontSize: 6, color: "text.primary" }} />
                  </ListItemIcon>
                  <Link href={topic.route}>
                    <a onClick={() => setActiveTopic(topic)}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          fontWeight: 600,
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {topic.name}
                      </Typography>
                    </a>
                  </Link>
                </ListItem>
              ) : (
                <></>
              )
            )}
          </List>
        </Box>
      </ContentLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  params,
}) => {
  try {
    res.setHeader(
      "Cache-Control",
      "public, s-maxage=10, stale-while-revalidate=59"
    );

    // If params is `null` return 404 page
    if (!params?.courseSlug || !params?.moduleSlug)
      return {
        notFound: true,
      };

    const service = new (
      await import("./../../../../src/common/service/baseServerSideService")
    ).default(req);
    const { data: module } = await service.get(
      `${SERVER}/api/courses/${params.courseSlug}/modules/${params.moduleSlug}/`
    );

    if (!module) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        module,
        params,
      },
    };
  } catch (error) {
    return onError(error);
  }
};
